import React, { useEffect, useState } from 'react'
import { useRouter } from 'next/router'
import { DateTime } from 'luxon'

import { OrderedEnum } from '@interfaces/OrderedEnum'
import { SearchByEnum } from '@interfaces/SearchByEnum'
import { useSearch } from '@hooks/search/useSearch'
import { usePagination } from '@hooks/usePagination'
import { useAuthentication } from '@hooks/useAuthentication'
import { RecommendTypeSearchEnum } from '@interfaces/RecommendTypeSearchEnum'
import { BookEnum } from '@interfaces/BookEnum'
import { scrollToElement } from '@lib/utils'
import { useLocalSearchHistory } from '@hooks/useSearchHistory'
import { SearchPageProps, WithSearchPageProps } from './interface'
import { SearchAllFormType } from '../../components/AdvanceSearchForm/interface'

export function withSearchPage(Component: React.FC<SearchPageProps>) {
  function WithSearchPage({
    text,
    tab,
    advanceSearch,
    secondaryType,
    orderBy,
  }: WithSearchPageProps) {
    const router = useRouter()
    const { page, perpage, pageChange, perpageChange } = usePagination()
    const { setLocalSearchHistory } = useLocalSearchHistory()
    const { isAuthenticated, isUserLoading } = useAuthentication()

    const [searchParam, setSearchParam] = useState<SearchAllFormType>({
      bookType: [],
      categoryIds: [],
      searchText: '',
      secondaryType: undefined,
      orderBy: OrderedEnum.NEW_OLD,
    })

    const [showAdvanceSearch, setShowAdvanceSearch] =
      useState<boolean>(advanceSearch)
    const [searchBy, setSearchBy] = useState<SearchByEnum>(tab)

    async function handleAdvanceSearchChange() {
      setShowAdvanceSearch((prev: boolean) => !prev)

      await router.replace({
        pathname: router.pathname,
        query: {
          text,
          tab,
          advanceSearch: !showAdvanceSearch,
        },
      })
    }

    async function handleSearchBy(key: string) {
      setSearchBy(key as SearchByEnum)
      pageChange(1)

      await router.replace({
        pathname: router.pathname,
        query: { ...router.query, tab: key },
      })
    }

    function handlePageChange(newPage: number) {
      pageChange(newPage)
      scrollToElement('#search-page')
    }

    const {
      isLoading,
      handleLikeBook,
      isLoadingToggleBookLike,
      data: booksData,
    } = useSearch({
      limitPerPage: perpage,
      page,
      bookType: showAdvanceSearch ? searchParam.bookType : undefined,
      categoryIds: showAdvanceSearch
        ? searchParam.categoryIds.map(i => Number(i))
        : undefined,
      isEbook: undefined,
      searchText: searchParam.searchText,
      isEnded:
        showAdvanceSearch &&
        searchParam.secondaryType === RecommendTypeSearchEnum.ENDED
          ? true
          : searchParam.secondaryType === RecommendTypeSearchEnum.NOT_END
          ? false
          : undefined,
      secondaryType: showAdvanceSearch ? searchParam.secondaryType : undefined,
      orderBy: showAdvanceSearch
        ? searchParam.orderBy === OrderedEnum.OLD_NEW
          ? OrderedEnum.NEW_OLD
          : searchParam.orderBy === OrderedEnum.POPULAR_LESS
          ? OrderedEnum.POPULAR
          : searchParam.orderBy
        : OrderedEnum.NEW_OLD,
      orderDirection:
        showAdvanceSearch &&
        (searchParam.orderBy === OrderedEnum.OLD_NEW ||
          searchParam.orderBy === OrderedEnum.POPULAR_LESS)
          ? 'ASC'
          : 'DESC',
      searchBy,
    })

    async function handleSubmit(val: SearchAllFormType) {
      if (page !== 1) {
        pageChange(1)
      }

      const query: any = {
        text: encodeURIComponent(val.searchText.trim()),
        tab: searchBy,
        advanceSearch: showAdvanceSearch,
        orderBy: val.orderBy,
      }
      if (val.bookType.length) query.bookType = val.bookType
      if (val.categoryIds.length) query.categoryIds = val.categoryIds
      if (val.secondaryType) query.secondaryType = val.secondaryType

      if (!isAuthenticated && !isUserLoading && !!val.searchText.trim()) {
        setLocalSearchHistory(val.searchText.trim(), DateTime.now().toISO())
      }

      await router.replace({
        pathname: router.pathname,
        query,
      })
    }

    useEffect(() => {
      setSearchBy(tab)
    }, [tab])

    useEffect(() => {
      const bookType = router.query.bookType
        ? typeof router.query.bookType === 'string'
          ? ([router.query.bookType] as BookEnum[])
          : (router.query.bookType as BookEnum[])
        : []
      const categoryIds = router.query.categoryIds
        ? typeof router.query.categoryIds === 'string'
          ? [router.query.categoryIds]
          : router.query.categoryIds
        : []

      setSearchParam(prev => ({
        ...prev,
        bookType,
        categoryIds,
        searchText: decodeURIComponent(text),
        secondaryType,
        orderBy,
      }))
    }, [
      text,
      router.query.bookType,
      router.query.categoryIds,
      secondaryType,
      orderBy,
    ])

    const newProps = {
      initialValues: searchParam,
      books: booksData?.data || [],
      page,
      perpage,
      total: booksData?.total || 0,
      showAdvanceSearch,
      isLoading: isLoading || isUserLoading,
      isLoadingToggleBookLike,
      handleSubmit,
      handlePageChange,
      perpageChange,
      handleAdvanceSearchChange,
      handleSearchBy,
      handleLikeBook,
    }

    return <Component {...newProps} />
  }

  return WithSearchPage
}
